import * as React from 'react'

import { cn } from '~/styled'

import { Table } from '../table/Table'
import { useDataTable } from './DataTableContext'
import { DataTableHeaderCell } from './DataTableHeaderCell'
import { DataTableSelectAllRowsCheckbox } from './DataTableSelectAllRowsCheckbox'

type DataTableHeadProps = Omit<
  React.ComponentProps<typeof Table.Header>,
  'children'
> & {
  sortable?: boolean
  isSticky?: boolean
  stickyOffset?: number
  showTotalInFirstColumn?: boolean
  totalSuffix?: string
}

export const DataTableHead = ({
  sortable = true,
  theme = 'light',
  isSticky = false,
  stickyOffset = 0,
  showTotalInFirstColumn,
  totalSuffix,
  style,
  className,
  ...props
}: DataTableHeadProps): React.ReactElement<unknown> => {
  const {
    getHeaderGroups,
    setIsSortable,
    enableRowSelection,
    getCanSomeRowsExpand,
    tablePosition,
    setTablePosition
  } = useDataTable()

  React.useEffect(() => {
    setIsSortable(sortable)
  }, [sortable, setIsSortable])

  const tableHeaderRowRef = React.useRef<HTMLTableRowElement>(null)
  const tableRef = React.useRef<HTMLTableElement | null>(null)
  const headerOffsetRef = React.useRef(0)

  const [headerOffset, setHeaderOffset] = React.useState<number>(0)
  const [headerHide, setHeaderHide] = React.useState<boolean>(false)

  const tableHeadElement = tableHeaderRowRef?.current?.closest('thead')

  if (tableHeadElement && tablePosition?.offsetTop === null) {
    const tableHeadHeight = tableHeadElement.getBoundingClientRect().height
    const intersectionOffset = stickyOffset + tableHeadHeight
    if (tablePosition) {
      setTablePosition({ ...tablePosition, offsetTop: intersectionOffset || 0 })
    }
  }

  React.useEffect(() => {
    if (!isSticky) return

    if (!tableRef.current && tableHeaderRowRef.current) {
      tableRef.current = tableHeaderRowRef.current.closest(
        'table'
      ) as HTMLTableElement | null
    }

    if (tableRef.current) {
      if (tablePosition?.top !== null && tablePosition?.bottom !== null) {
        const newOffset =
          tablePosition?.top != null &&
          tablePosition?.bottom != null &&
          tablePosition.top < stickyOffset &&
          tablePosition.bottom > 100
            ? -tablePosition.top + stickyOffset
            : 0

        if (newOffset !== 0) setHeaderHide(true)

        headerOffsetRef.current = newOffset
        setHeaderOffset(newOffset)

        requestAnimationFrame(() => {
          setHeaderHide(false)
        })
      }
    }
  }, [isSticky, stickyOffset, tablePosition])

  return (
    <Table.Header
      theme={theme}
      style={{
        ...style,
        '--header-offset': `${headerOffset}px`
      }}
      {...props}
      className={cn(
        'relative',
        'z-3',
        '[&_tr:not(:last-child)>th]:border-b-none',
        '[&_tr:not(:first-child)>th]:rounded-none!',
        className
      )}
    >
      {getHeaderGroups().map((headerGroup) => {
        return (
          <Table.Row
            key={headerGroup.id}
            ref={tableHeaderRowRef}
            className={
              isSticky && headerOffset !== 0
                ? cn(
                    'translate-y-(--header-offset)',
                    headerHide
                      ? 'delay-0ms opacity-0 transition-none'
                      : 'delay-150ms opacity-100 transition-opacity duration-100',
                    '[&_th]:last:before:absolute',
                    '[&_th]:last:before:bottom-0',
                    '[&_th]:last:before:pointer-events-none',
                    '[&_th]:last:before:right-0',
                    '[&_th]:last:before:shadow-sm',
                    '[&_th]:last:before:top-0',
                    '[&_th]:last:before:w-full'
                  )
                : undefined
            }
          >
            {getCanSomeRowsExpand() && <Table.HeaderCell className="w-10" />}
            {enableRowSelection && (
              <Table.HeaderCell className="w-10">
                <DataTableSelectAllRowsCheckbox />
              </Table.HeaderCell>
            )}
            {headerGroup.headers.map((header, index) => (
              <DataTableHeaderCell
                header={header}
                key={header.id}
                includeTotal={index === 0 && showTotalInFirstColumn}
                totalSuffix={totalSuffix}
              />
            ))}
          </Table.Row>
        )
      })}
    </Table.Header>
  )
}
